// Systems: where data lives (ERP, CRM, the warehouse). They are found in the data — the sender's explicit `system`
// (ingest meta), else naming conventions: a process "[ERP] Sales Orders to WMS" belongs to ERP, a connection
// "[FIN] Maventa REST" points to FIN, else the start of the message class — and described by the admins in the system
// registry (docs/systems.md). The registry is loaded once before the app renders (lib/systemsdata.tsx) and fills the
// module registry here, so systemName / systemColor / systemLogo and every SystemBadge say what the admins set, and an
// alias (DYN for ERP) is its system wherever a code turns up, without passing the registry to every call site.
import { namesTechnical } from './namemode.ts';
import type { Hit } from './api.ts';

export type SystemStatus = 'new' | 'checked' | 'hidden';

/** A system of the registry as the Cloud sends it (GET /api/tenants/{tid}/systems). */
export interface SystemInfo {
  code: string; name: string; kind: string | null; color: string | null; icon: string | null;
  /** The admins' grouping ("Monitoring", "Business") a view can be filtered by; null = none. */
  group: string | null;
  description: string | null; owner: string | null; vendor: string | null; url: string | null;
  aliases: string[]; status: SystemStatus; source: 'discovered' | 'manual';
  /** Address of the logo (changes with it), or null. */
  logo: string | null;
  firstSeenAt: number; reviewedAt: number | null; reviewedBy: string | null; updatedAt: number | null; updatedBy: string | null;
  /** The data models in it (when the Cloud detected them). */
  classes: string[];
  /** The connections (technical routes) its documents came or went over, busiest first (the detailed answer only). */
  connections?: SystemRoute[];
  /** Connections whose [PREFIX] names another system than the senders' explicit system says (the detailed answer only). */
  warnings?: { connection: string; namedFor: string; dataSays: string }[];
}

/** A connection seen with a system: one system has many routes in (OData, SQL, REST…), a connection points to one system. */
export interface SystemRoute { name: string; count: number; last: number | null; namedFor: string | null }

/** The defaults of the usual codes, for a code the registry does not have (or before it is loaded). */
const KNOWN: Record<string, { name: string; color: string }> = {
  WEB: { name: 'Webshop', color: '#2F4BE8' },
  ERP: { name: 'ERP', color: '#475569' },
  WMS: { name: 'Warehouse', color: '#7C4DDB' },
  CARRIER: { name: 'Carrier', color: '#C2410C' },
  CAR: { name: 'Carrier', color: '#C2410C' },
  FIN: { name: 'Finance', color: '#0B7A55' },
  CRM: { name: 'CRM', color: '#B4237A' },
  DWH: { name: 'Data warehouse', color: '#0E7490' },
  EDI: { name: 'EDI', color: '#C2410C' },
  ECB: { name: 'ECB rates', color: '#0369A1' },
  HR: { name: 'HR', color: '#BE123C' },
  PAY: { name: 'Payroll', color: '#0D9488' },
  FAC: { name: 'Building automation', color: '#65A30D' },
  ESG: { name: 'Energy reporting', color: '#16A34A' },
};
const PALETTE = ['#2F4BE8', '#7C4DDB', '#C2410C', '#0B7A55', '#B4237A', '#0E7490', '#A16207', '#475569'];

// ---------------------------------------------------------------- the registry

let systems = new Map<string, SystemInfo>();
let aliases = new Map<string, string>();

/** Replaces the registry (every system of the tenant). */
export function setSystems(list: Iterable<SystemInfo>): void {
  const next = new Map<string, SystemInfo>();
  const al = new Map<string, string>();
  for (const s of list) {
    next.set(s.code, s);
    for (const a of s.aliases) al.set(a, s.code);
  }
  systems = next;
  aliases = al;
}

/** Puts one edited system in place (`was` = its code before, when the code changed); merged aliases leave the registry. */
export function setSystem(s: SystemInfo, was?: string): void {
  const list = [...systems.values()].filter((x) => x.code !== s.code && x.code !== was && !s.aliases.includes(x.code));
  setSystems([...list, s]);
}

/** Every system of the registry, by code. */
export const registeredSystems = (): ReadonlyMap<string, SystemInfo> => systems;

/** The system a code means: an alias is its system; anything else is itself. */
export function resolveSystem(code: string): string {
  return aliases.get(code.trim().toUpperCase()) ?? code;
}

/** The registry's entry for a code (or its alias), if any. */
export function systemInfo(code: string): SystemInfo | undefined {
  return systems.get(resolveSystem(code));
}

// ---------------------------------------------------------------- codes in the data

/** A system code as senders give it: 1–24 of A–Z a–z 0–9 _ . - (the Vault's rule). */
export const SYSTEM_CODE = /^[A-Za-z0-9_.-]{1,24}$/;

const BRACKET = /^\s*\[([^\]]{1,24})\]\s*/;
/** Roles a process's name may start with: not systems (the cloud's prefix.ts has the same list). */
const ROLES = /^(sub|main|sub-?main|subprocess|child|parent|test|test-?run|util|utils|utility|common|shared|helper|wip|old|deprecated|dev|draft)$/i;

/** The system code a name's leading brackets give, passing over roles ("[Sub] …" names none), or null. */
export function prefixCode(name: string | undefined | null): string | null {
  let rest = name ?? '';
  for (let i = 0; i < 4; i++) {
    const m = BRACKET.exec(rest);
    if (!m) return null;
    const code = m[1].trim();
    if (!ROLES.test(code)) return code.toUpperCase();
    rest = rest.slice(m[0].length);
  }
  return null;
}

/** The system of a [PREFIX] name ("[ERP] Sales Orders to WMS" → ERP; an alias → its system), or null. */
export function systemOf(name: string | undefined | null): string | null {
  const code = prefixCode(name);
  return code ? resolveSystem(code) : null;
}

/** Name without its leading [PREFIX] and [Role] brackets. */
export function stripSystem(name: string | undefined | null): string {
  if (namesTechnical()) return name ?? '';
  let out = name ?? '';
  for (let i = 0; i < 4 && BRACKET.test(out); i++) out = out.replace(BRACKET, '');
  return out;
}

/** The system of one document: its explicit `system`, else the [PREFIX] of its process or connection, else the start of its class. */
export function messageSystem(meta: Record<string, string | undefined>, cls: string): string {
  const explicit = meta.system?.trim();
  if (explicit && SYSTEM_CODE.test(explicit)) return resolveSystem(explicit.toUpperCase());
  return systemOf(meta.process) ?? systemOf(meta.connection) ?? resolveSystem(cls.slice(0, 3).toUpperCase());
}

export function hitSystem(h: Hit): string {
  return messageSystem(h.meta, h.messageClass);
}

// ---------------------------------------------------------------- how a system is shown

export function systemName(code: string): string {
  if (namesTechnical()) return resolveSystem(code);
  const s = systemInfo(code);
  if (s) return s.name;
  const c = resolveSystem(code);
  return KNOWN[c]?.name ?? c.charAt(0) + c.slice(1).toLowerCase();
}

/** The colour of a system: the admin's, else a known one, else one of the palette picked by the code. */
export function systemColor(code: string): string {
  const c = resolveSystem(code);
  const s = systems.get(c);
  if (s?.color) return s.color;
  return defaultSystemColor(c);
}

/** The colour a system has when nobody chose one. */
export function defaultSystemColor(code: string): string {
  if (KNOWN[code]) return KNOWN[code].color;
  let h = 0;
  for (const ch of code) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length]!;
}

/** The address of a system's logo, or null. */
export const systemLogo = (code: string): string | null => systemInfo(code)?.logo ?? null;

/** The icon chosen for a system (components/icons.tsx SYSTEM_ICON), or null. */
export const systemIcon = (code: string): string | null => systemInfo(code)?.icon ?? null;

/** Short label for a system badge: the system's code (an alias says its system), at most 4 characters as they are, else 3. */
export function systemCode(code: string): string {
  const c = resolveSystem(code);
  return c.length > 4 ? c.slice(0, 3) : c;
}
